import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { ArrowLeft, CalendarCheck, ClipboardPen, Drama, Shirt } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'

interface Step {
    title: string
    text: string
    icon: LucideIcon
}

const STEPS: Step[] = [
    {
        title: 'משאירים פרטים',
        text: 'ממלאים את הטופס הקצר בתחתית העמוד. זה לוקח פחות משתי דקות.',
        icon: ClipboardPen
    },
    {
        title: 'מתאמים מועד',
        text: 'חוזרים אליכם, עונים על כל השאלות ומוצאים יחד את הקבוצה שמתאימה לגיל ולניסיון.',
        icon: CalendarCheck
    },
    {
        title: 'עולים לבמה',
        text: 'מגיעים לשיעור, משחקים עם הקבוצה ומרגישים איך זה מבפנים. אחרי השיעור מחליטים יחד, בלי לחץ.',
        icon: Drama
    }
]

const StepsSection: FC = (): ReactNode => {
    return (
        <section id='how' aria-labelledby='how-title' className='scroll-mt-16 py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal className='max-w-2xl'>
                    <p className='text-sm font-medium text-primary'>
                        שיעור ניסיון
                    </p>

                    <h2 id='how-title' className='mt-3 font-heading text-4xl font-bold leading-tight text-balance sm:text-5xl'>
                        שלושה צעדים עד הבמה
                    </h2>

                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        הדרך הכי טובה לדעת אם תיאטרון מתאים לילד או לילדה שלכם היא פשוט לנסות. כך זה עובד:
                    </p>
                </Reveal>

                <ol className='mt-12 grid gap-5 md:grid-cols-3'>
                    {STEPS.map((step, index) => {
                        const Icon = step.icon

                        return (
                            <li key={step.title}>
                                <Reveal delay={index * 120} className='h-full'>
                                    <div className='relative h-full rounded-3xl border border-border bg-card/60 p-7'>
                                        <span aria-hidden='true' className='absolute end-6 top-5 font-heading text-6xl font-bold leading-none text-primary/15'>
                                            {index + 1}
                                        </span>

                                        <span className='flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground'>
                                            <Icon aria-hidden='true' className='size-6' />
                                        </span>

                                        <h3 className='mt-5 font-heading text-2xl font-bold'>
                                            <span className='sr-only'>
                                                {`שלב ${index + 1}: `}
                                            </span>
                                            {step.title}
                                        </h3>

                                        <p className='mt-2.5 leading-relaxed text-muted-foreground text-pretty'>
                                            {step.text}
                                        </p>
                                    </div>
                                </Reveal>
                            </li>
                        )
                    })}
                </ol>

                <Reveal className='mt-6'>
                    <div className='flex flex-col gap-5 rounded-3xl border border-primary/25 bg-primary/[0.06] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7'>
                        <div className='flex items-start gap-4'>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary'>
                                <Shirt aria-hidden='true' className='size-5' />
                            </span>

                            <p className='leading-relaxed text-foreground/85'>
                                <span className='font-semibold text-foreground'>
                                    מה להביא?
                                </span>
                                {' '}
                                בגדים נוחים שאפשר לזוז בהם, בקבוק מים והרבה סקרנות.
                            </p>
                        </div>

                        <a
                            href='#trial'
                            className='inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                        >
                            להשארת פרטים
                            <ArrowLeft aria-hidden='true' className='size-4' />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default StepsSection
